import { Routes } from '@angular/router'
import { authGuard } from './core/guards/auth.guard'
import { roleGuard } from './core/guards/role.guard'
import { activityRouteData } from './core/activity/activity-route-data'
import { unappliedChangesGuard } from './features/editor/shared/unapplied-changes.guard'

export const routes: Routes = [
  // ── Unauthenticated routes ──────────────────────────────────────────────────
  {
    // Login screen: shown to unauthenticated users; redirects to Cognito Hosted UI.
    path: 'login',
    loadComponent: () =>
      import('./features/login/login.component').then(m => m.LoginComponent),
  },
  {
    // OAuth callback: Cognito redirects here after a successful login.
    // Amplify processes the authorization code and AuthCallbackComponent
    // populates the session, then navigates to the app root.
    path: 'auth/callback',
    loadComponent: () =>
      import('./features/auth-callback/auth-callback.component').then(m => m.AuthCallbackComponent),
  },

  // ── Authenticated routes ────────────────────────────────────────────────────
  {
    path: 'account/change-password',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/change-password/change-password.component').then(m => m.ChangePasswordComponent),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/role-selector/role-selector.component').then(m => m.RoleSelectorComponent),
  },
  {
    path: 'admin/tasks/new',
    canActivate: [authGuard, roleGuard('Admin')],
    loadComponent: () =>
      import('./features/admin/task-new/admin-task-new.component').then(m => m.AdminTaskNewComponent),
  },
  {
    path: 'admin',
    canActivate: [authGuard, roleGuard('Admin')],
    loadComponent: () =>
      import('./features/admin/admin-shell.component').then(m => m.AdminShellComponent),
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/admin/dashboard/admin-dashboard.component').then(m => m.AdminDashboardComponent),
      },
      {
        path: 'tasks',
        loadComponent: () =>
          import('./features/admin/task-management/admin-task-management.component').then(m => m.AdminTaskManagementComponent),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/admin/user-management/admin-user-management.component').then(m => m.AdminUserManagementComponent),
      },
      {
        path: 'export',
        loadComponent: () =>
          import('./features/admin/export/admin-export.component').then(m => m.AdminExportComponent),
      },
      {
        path: 'import',
        loadComponent: () =>
          import('./features/admin/import/admin-import.component').then(m => m.AdminImportComponent),
      },
      {
        path: 'road-segments/ingest',
        loadComponent: () =>
          import('./features/admin/ingest/admin-ingest.component').then(m => m.AdminIngestComponent),
      },
      {
        path: 'road-segments/projects',
        loadComponent: () =>
          import('./features/admin/projects/admin-projects.component').then(m => m.AdminProjectsComponent),
      },
      {
        path: 'road-segments/ingest-from-s3',
        loadComponent: () =>
          import('./features/admin/ingest-from-s3/admin-ingest-s3.component').then(m => m.AdminIngestS3Component),
      },
    ],
  },
  {
    path: 'reviewer',
    canActivate: [authGuard, roleGuard('Reviewer')],
    loadComponent: () =>
      import('./features/reviewer/reviewer-task-list/reviewer-task-list.component').then(m => m.ReviewerTaskListComponent),
  },
  {
    path: 'reviewer/task/:taskId',
    canActivate: [authGuard, roleGuard('Reviewer')],
    loadComponent: () =>
      import('./features/reviewer/reviewing/reviewing.component').then(m => m.ReviewingComponent),
  },
  {
    // The Editor home page. Deliberately NOT tracked (no activityRouteData): the
    // tracker deactivates here and records the hop as route.change → other.
    path: 'editor',
    canActivate: [authGuard, roleGuard('Editor')],
    loadComponent: () =>
      import('./features/editor/home/editor-home.component').then(m => m.EditorHomeComponent),
  },
  {
    // Optional ?task=<id> brings that task's card into view once (the home page's Needs Fixes rows).
    path: 'editor/tasks',
    canActivate: [authGuard, roleGuard('Editor')],
    data: activityRouteData('task_list'),
    loadComponent: () =>
      import('./features/editor/task-list/editor-task-list.component').then(m => m.EditorTaskListComponent),
  },
  {
    // Inspection Group review, step 1: the project list (the latest version of
    // each project). Same activity surface as step 2, so the hop between them
    // records no route.change; the pick is its own event (group.project.pick).
    // The list handles none of the review's keys, so none counts as a shortcut here.
    path: 'editor/inspection-groups',
    canActivate: [authGuard, roleGuard('Editor')],
    data: activityRouteData('group_review', { shortcuts: false }),
    loadComponent: () =>
      import('./features/editor/inspection-group-review/project-list/inspection-group-project-list.component')
        .then(m => m.InspectionGroupProjectListComponent),
  },
  {
    // Step 2: one project's shared, lease-based queue.
    path: 'editor/inspection-groups/:project',
    canActivate: [authGuard, roleGuard('Editor')],
    data: activityRouteData('group_review'),
    loadComponent: () =>
      import('./features/editor/inspection-group-review/inspection-group-review.component').then(m => m.InspectionGroupReviewComponent),
  },
  {
    path: 'editor/task/:taskId',
    canActivate: [authGuard, roleGuard('Editor')],
    canDeactivate: [unappliedChangesGuard],
    data: activityRouteData('segment_editing'),
    loadComponent: () =>
      import('./features/editor/editing/editing.component').then(m => m.EditingComponent),
  },
  {
    path: 'editor/task/:taskId/markup',
    canActivate: [authGuard, roleGuard('Editor')],
    canDeactivate: [unappliedChangesGuard],
    data: activityRouteData('markup'),
    loadComponent: () =>
      import('./features/editor/markup-editing/markup-editing.component').then(m => m.MarkupEditingComponent),
  },
  {
    path: 'editor/task/:taskId/quick-edit',
    canActivate: [authGuard, roleGuard('Editor')],
    canDeactivate: [unappliedChangesGuard],
    data: activityRouteData('quick_edit'),
    loadComponent: () =>
      import('./features/editor/quick-editing/quick-editing.component').then(m => m.QuickEditingComponent),
  },
  {
    path: 'viewer',
    canActivate: [authGuard, roleGuard('Viewer')],
    loadComponent: () =>
      import('./features/viewer/viewer.component').then(m => m.ViewerComponent),
  },
  {
    path: 'supervisor',
    canActivate: [authGuard, roleGuard('EditorSupervisor')],
    loadComponent: () =>
      import('./features/supervisor/editor-list/supervisor-editor-list.component').then(m => m.SupervisorEditorListComponent),
  },
  {
    path: 'supervisor/editor/:userId',
    canActivate: [authGuard, roleGuard('EditorSupervisor')],
    loadComponent: () =>
      import('./features/supervisor/editor-detail/supervisor-editor-detail.component').then(m => m.SupervisorEditorDetailComponent),
  },
  {
    path: 'supervisor/time-use',
    canActivate: [authGuard, roleGuard('EditorSupervisor')],
    loadComponent: () =>
      import('./features/supervisor/time-use/supervisor-time-use.component').then(m => m.SupervisorTimeUseComponent),
  },
  {
    path: 'supervisor/task/:taskId',
    canActivate: [authGuard, roleGuard('EditorSupervisor')],
    loadComponent: () =>
      import('./features/supervisor/task-overview/supervisor-task-overview.component').then(m => m.SupervisorTaskOverviewComponent),
  },
  {
    path: 'supervisor/group/:groupId',
    canActivate: [authGuard, roleGuard('EditorSupervisor')],
    loadComponent: () =>
      import('./features/supervisor/group-review/supervisor-group-review.component').then(m => m.SupervisorGroupReviewComponent),
  },
]
